'use client';

import { CalloutPlugin } from '@udecode/plate-callout/react';
import { CodeBlockPlugin } from '@udecode/plate-code-block/react';
import { DatePlugin } from '@udecode/plate-date/react';
import {
  FontBackgroundColorPlugin,
  FontColorPlugin,
  FontFamilyPlugin,
  FontSizePlugin,
} from '@udecode/plate-font/react';
import { HorizontalRulePlugin } from '@udecode/plate-horizontal-rule/react';
import { KbdPlugin } from '@udecode/plate-kbd/react';
import { ColumnPlugin } from '@udecode/plate-layout/react';
import { SlashPlugin } from '@udecode/plate-slash-command/react';
import { TogglePlugin } from '@udecode/plate-toggle/react';
import { TrailingBlockPlugin } from '@udecode/plate-trailing-block';

import { MarkdownPlugin } from '@udecode/plate-markdown';
import { alignPlugin } from './align-plugin';
import { autoformatPlugin } from './autoformat-plugin';
import { basicNodesPlugins } from './basic-nodes-plugins';
import { blockMenuPlugins } from './block-menu-plugins';
import { createCopilotPlugin, copilotHandlersPlugin } from './copilot-plugin';
import { cursorOverlayPlugin } from './cursor-overlay-plugin';
import { CustomAIPlugin } from './custom-ai-plugin';
import { deletePlugins } from './delete-plugins';
import { dndPlugins } from './dnd-plugins';
import { exitBreakPlugin } from './exit-break-plugin';
import { indentListPlugins } from './indent-list-plugins';
import { lineHeightPlugin } from './line-height-plugin';
import { linkPlugin } from './link-plugin';
import { mentionPlugin } from './mention-plugin';
import { resetBlockTypePlugin } from './reset-block-type-plugin';
import { skipMarkPlugin } from './skip-mark-plugin';
import { softBreakPlugin } from './soft-break-plugin';
import { tablePlugin } from './table-plugin';
import { tocPlugin } from './toc-plugin';

export const viewPlugins = [
  ...basicNodesPlugins,
  HorizontalRulePlugin,
  linkPlugin,
  DatePlugin,
  mentionPlugin,
  tablePlugin,
  TogglePlugin,
  tocPlugin,
  CalloutPlugin,
  ColumnPlugin,

  // Marks
  FontColorPlugin,
  FontBackgroundColorPlugin,
  FontFamilyPlugin,
  FontSizePlugin,
  KbdPlugin,
  skipMarkPlugin,

  // Block Style
  alignPlugin,
  ...indentListPlugins,
  lineHeightPlugin,
] as const;

/**
 * Create editor plugins with optional AI features based on license
 */
export function createEditorPlugins(hasAIFeatures: boolean = false, copilotOptions: any = {}) {
  const plugins = [
    // Nodes
    ...viewPlugins,

    // Functionality
    SlashPlugin.extend({
      options: {
        triggerQuery(editor) {
          return !editor.api.some({
            match: { type: editor.getType(CodeBlockPlugin) },
          });
        },
      },
    }),
    autoformatPlugin,
    cursorOverlayPlugin,
    ...blockMenuPlugins,
    ...dndPlugins,
    exitBreakPlugin,
    resetBlockTypePlugin,
    ...deletePlugins,
    softBreakPlugin,
    TrailingBlockPlugin,
  ];

  // Only add AI plugin if user has AI features
  if (hasAIFeatures) {
    plugins.unshift(createCopilotPlugin(copilotOptions) as any);
    plugins.push(CustomAIPlugin as any);
    plugins.push(MarkdownPlugin as any);
    plugins.push(copilotHandlersPlugin as any);
  }

  return plugins.filter(Boolean);
}

export const editorPlugins = [
  // Nodes
  ...viewPlugins,

  // Functionality
  SlashPlugin.extend({
    options: {
      triggerQuery(editor) {
        return !editor.api.some({
          match: { type: editor.getType(CodeBlockPlugin) },
        });
      },
    },
  }),
  autoformatPlugin,
  cursorOverlayPlugin,
  ...blockMenuPlugins,
  ...dndPlugins,
  exitBreakPlugin,
  resetBlockTypePlugin,
  ...deletePlugins,
  softBreakPlugin,
  TrailingBlockPlugin,

  // Custom AI plugin - loaded after selection and other core functionality
  // Custom AI plugin - loaded after selection and other core functionality
  CustomAIPlugin,
  MarkdownPlugin,

  // UI
  // FixedToolbarPlugin is now created and added conditionally in the PlateEditor component
  // using createFixedToolbarPlugin with save button configuration
  // FloatingToolbarPlugin is added in useCreateEditor
];
